import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  lazy,
  Suspense,
  useCallback,
} from "react";
import { Button, Typography, message, Space, Skeleton, Tabs } from "antd";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Lightbulb,
  MessageSquare,
  ArrowBigLeft
} from "lucide-react";
import {
  getModelColor,
  getModelDisplayName,
  normalizeModelKey,
} from "../../constants/modelMeta";
import "../DataHistory/Ensemble/EnsembleIndex.css";
import RandomSamplingTable from "./RandomSampling/RandomSamplingTable";
import { useSelector } from "react-redux";
const Insights = lazy(() => import("./Insights/Insights"));
const XychatAssistant = lazy(() => import("./Chatbot/XychatAssistant"));
const ScatterChart = lazy(() => import("../charts/ModalCharts/ZscoreChart"));
const LocalOutlierScatterChart = lazy(() =>
  import("../charts/ModalCharts/LocalOutlierChart")
);
const RsfChart = lazy(() => import("../charts/ModalCharts/RsfChart"));
const BenfordChart = lazy(() => import("../charts/ModalCharts/BenfordChart"));
const PdfChart = lazy(() => import("../charts/ModalCharts/PdfChart"));
const MonteCarloChart = lazy(() =>
  import("../charts/ModalCharts/MonteCarloChart")
);
const EnsembleIndex = lazy(() => import("./Ensemble/EnsembleIndex"));

const { Text } = Typography;

// Utility function to convert names for display
const convertToDisplayName = (name) => {
  if (!name) return "";
  // Convert "Unit_Cost" -> "Unit Cost", "Item_Branch" -> "Item Branch"
  return name.replace(/_/g, " ");
};

// Utility function to check if backend name matches data name
const namesMatch = (backendName, dataName) => {
  if (!backendName || !dataName) return false;
  const backendNormalized = backendName.replace(/\s+/g, "_");
  return backendNormalized === dataName;
};

const AnalyticsDashboard = ({ applyResponse }) => {
  const navigate = useNavigate();
  const generativeEngineInsightsResult = useSelector((s) => s.file?.generativeEngineInsightsResult);
  const [processedData, setProcessedData] = useState([]);

  // User-selected values (ALWAYS use data format with underscores)
  const [userDepVariable, setUserDepVariable] = useState(null);
  const [userGroupByColumns, setUserGroupByColumns] = useState([]);

  // Initialize - but don't force backend values, just suggest them
  useEffect(() => {
    if (!applyResponse) return;

    const backendDep =
      applyResponse.dependent_column || applyResponse.dep_variable || null;
    const backendGroupBy = Array.isArray(applyResponse.grp_by_columns)
      ? applyResponse.grp_by_columns
      : Array.isArray(applyResponse.groupby_columns)
      ? applyResponse.groupby_columns
      : [];

    if (!userDepVariable && backendDep) {
      // Try to find matching column in available data
      const suggestedDep = findMatchingColumn(backendDep, availableColumns);
      if (suggestedDep) {
        setUserDepVariable(suggestedDep);
      }
    }

    if (userGroupByColumns.length === 0 && backendGroupBy.length > 0) {
      const suggestedGroupBy = backendGroupBy
        .map((col) => findMatchingColumn(col, availableColumns))
        .filter(Boolean);
      if (suggestedGroupBy.length > 0) {
        setUserGroupByColumns(suggestedGroupBy);
      }
    }
  }, [applyResponse, processedData]); // Add processedData to refresh when data arrives

  // Helper function to find matching column
  const findMatchingColumn = (backendName, availableColumns) => {
    if (!backendName || !availableColumns.length) return null;

    const backendNormalized = backendName.replace(/\s+/g, "_");

    // Exact match
    const exactMatch = availableColumns.find((col) => {
      const normalizedCol = col.replace(/_/g, "_");
      const normalizedBackend = backendNormalized.replace(/__+/g, "_");
      return normalizedCol === normalizedBackend;
    });

    if (exactMatch) return exactMatch;

    // Case-insensitive match
    const caseInsensitiveMatch = availableColumns.find(
      (col) => col.toLowerCase() === backendNormalized.toLowerCase()
    );

    if (caseInsensitiveMatch) return caseInsensitiveMatch;

    // Contains match
    const containsMatch = availableColumns.find(
      (col) =>
        col.toLowerCase().includes(backendNormalized.toLowerCase()) ||
        backendNormalized.toLowerCase().includes(col.toLowerCase())
    );

    return containsMatch || null;
  };

  // Use user selections (user has full control)
  const effectiveDepVariable = userDepVariable;
  const effectiveGroupByColumns = userGroupByColumns;

  const getInitialProcessedData = (resp) => {
    if (!resp) return [];
    return resp.sample_data || [];
  };

  // Initialize processedData from applyResponse whenever applyResponse changes
  useEffect(() => {
    const initial = getInitialProcessedData(applyResponse);
    if (Array.isArray(initial) && initial.length > 0) {
      setProcessedData(initial);
    } else {
      setProcessedData([]);
    }
  }, [applyResponse]);

  // Get available columns from actual data (with underscores)
  const availableColumns = useMemo(() => {
    if (processedData.length > 0) {
      const dataColumns = Object.keys(processedData[0]);
      return dataColumns;
    }

    // If no data yet, return empty array
    return [];
  }, [processedData]);

  // Get numeric columns from actual data
  const numericColumns = useMemo(() => {
    if (processedData.length === 0) {
      return [];
    }

    const sampleRow = processedData[0];
    const numericCols = Object.keys(sampleRow).filter((col) => {
      const val = sampleRow[col];

      if (val === null || val === undefined) return false;

      if (typeof val === "number" && Number.isFinite(val)) {
        return true;
      }

      if (typeof val === "string") {
        const cleanVal = val.replace(/,/g, "").trim();
        const parsed = parseFloat(cleanVal);
        return !Number.isNaN(parsed) && isFinite(parsed);
      }

      return false;
    });

    return numericCols;
  }, [processedData]);

  // Prepare scatter data - uses whatever user selected
  const scatterData = useMemo(() => {
    if (!effectiveDepVariable || processedData.length === 0) {
      return [];
    }

    // Check if selected dependent variable exists
    if (!availableColumns.includes(effectiveDepVariable)) {
      console.warn(
        `Selected dependent variable '${effectiveDepVariable}' not found in data`
      );
      return [];
    }

    const useGroupBy = effectiveGroupByColumns.length > 0;
    const categories = [];
    const groupToIndex = {};

    // Validate all selected group by columns exist
    if (useGroupBy) {
      const missingColumns = effectiveGroupByColumns.filter(
        (col) => !availableColumns.includes(col)
      );
      if (missingColumns.length > 0) {
        console.warn(`Some group by columns not found:`, missingColumns);
        // Continue with available columns only
        effectiveGroupByColumns = effectiveGroupByColumns.filter((col) =>
          availableColumns.includes(col)
        );
      }

      // Collect unique groups
      const groupSet = new Set();
      processedData.forEach((row) => {
        const keyParts = effectiveGroupByColumns.map((col) => {
          const raw = row[col];
          return raw !== undefined && raw !== null ? String(raw) : "N/A";
        });
        const groupKey = keyParts.join(" | ");
        groupSet.add(groupKey);
      });

      categories.push(...Array.from(groupSet).sort());
      categories.forEach((cat, idx) => {
        groupToIndex[cat] = idx;
      });
    }

    const scatterPoints = processedData
      .map((row, index) => {
        try {
          // Get dependent variable value
          const rawY = row[effectiveDepVariable];
          let y;

          if (typeof rawY === "number") {
            y = rawY;
          } else if (typeof rawY === "string") {
            y = parseFloat(rawY.replace(/,/g, ""));
          } else {
            y = NaN;
          }

          if (Number.isNaN(y)) {
            console.warn(
              `Invalid numeric value for ${effectiveDepVariable}:`,
              rawY
            );
            return null;
          }

          // Detect anomalies
          let exception = 0;
          const zScoreField =
            row.Z_SCORE || row.z_score || row["Z-SCORE"] || row.anomaly_score;
          const outlierField =
            row.Outlier_ZScore ||
            row.outlier_zscore ||
            row.is_anomaly ||
            row.Outlier_Benford;

          if (outlierField !== undefined) {
            exception = parseInt(outlierField) === 1 ? 1 : 0;
          } else if (zScoreField !== undefined) {
            const z = Math.abs(parseFloat(zScoreField));
            exception = z > 3 ? 1 : 0;
          }

          let xIndex, groupLabel;
          if (useGroupBy && effectiveGroupByColumns.length > 0) {
            const keyParts = effectiveGroupByColumns.map((col) => {
              const raw = row[col];
              return raw !== undefined && raw !== null ? String(raw) : "N/A";
            });
            groupLabel = keyParts.join(" | ");
            xIndex = groupToIndex[groupLabel];

            // Add jitter to prevent overlapping
            const jitter = (Math.random() - 0.5) * 0.6;
            return {
              x: xIndex + jitter,
              y: y,
              exception: exception,
              zScore: zScoreField || 0,
              group: groupLabel,
              row: row,
              categories: categories,
            };
          } else {
            // No grouping - use row index
            return {
              x: index + 1,
              y: y,
              exception: exception,
              zScore: zScoreField || 0,
              group: `Row ${index + 1}`,
              row: row,
              categories: [],
            };
          }
        } catch (error) {
          console.error("Error processing row:", error, row);
          return null;
        }
      })
      .filter(Boolean);

    return scatterPoints;
  }, [
    processedData,
    effectiveDepVariable,
    effectiveGroupByColumns,
    availableColumns,
  ]);

  // Read models robustly from applied_models OR models
  const rawModels =
    Array.isArray(applyResponse?.applied_models) &&
    applyResponse.applied_models.length > 0
      ? applyResponse.applied_models
      : Array.isArray(applyResponse?.models)
      ? applyResponse.models
      : [];

  const models = rawModels.map((m) => String(m).toUpperCase());

  // Chart render logic
  const renderChartEl = useMemo(() => {
    if (!scatterData || scatterData.length === 0) {
      return (
        <div className="text-white text-center p-8">
          <div className="text-lg mb-2">
            {effectiveDepVariable
              ? `Processing "${convertToDisplayName(
                  effectiveDepVariable
                )}" data... (${processedData.length} records loaded)`
              : "🕰️ One moment — polishing your chart gems..."}
          </div>
        </div>
      );
    }

    const isLOF =
      models.includes("LOF") ||
      models.includes("LOCAL_OUTLIER") ||
      models.includes("LOF_ANOMALY");
    const isRSF =
      models.includes("RSF") ||
      models.includes("RANDOM_SURVEST_FOREST") ||
      models.includes("RANDOM_SURVEST");
    const isBenford =
      models.includes("BENFORD") || models.includes("BENFORD_ANALYSIS");
    const isPDF =
      models.includes("PDF") ||
      models.includes("PDF_MODEL") ||
      models.includes("PROBABILITY_DENSITY") ||
      models.includes("pdf");
    const isMonteCarlo = models.includes("MONTECARLO");
    const isSampling = models.includes("SAMPLING");

    if (isLOF) {
      return (
        <Suspense fallback={<div className="w-full h-full animate-pulse" />}>
          <LocalOutlierScatterChart
            data={scatterData}
            dep={effectiveDepVariable}
            groupBy={effectiveGroupByColumns}
          />
        </Suspense>
      );
    }

    if (isRSF) {
      return (
        <Suspense fallback={<div className="w-full h-full animate-pulse" />}>
          <RsfChart
            data={scatterData}
            dep={effectiveDepVariable}
            groupBy={effectiveGroupByColumns}
          />
        </Suspense>
      );
    }

    if (isBenford) {
      // The BenfordChart expects an array of rows with fields:
      // { Actual, Actualvalue, Benford, ... } — map scatterData -> rows
      const benfordPayload = scatterData.map((pt) => {
        // prefer pt.row (raw server row) if present; otherwise pt itself
        const row = pt.row ?? pt;
        return {
          Actual: row.Actual ?? row.actual ?? row["Actual"] ?? null,
          Actualvalue:
            row.Actualvalue ??
            row.ActualValue ??
            row.actualvalue ??
            row.actualValue ??
            row.Actual_value ??
            row.ActualValue ??
            null,
          Benford: row.Benford ?? row.benford ?? null,
        };
      });

      return (
        <Suspense fallback={<div className="w-full h-full animate-pulse" />}>
          <BenfordChart
            data={benfordPayload}
            groupLabel={effectiveGroupByColumns.join(" | ") || "Group"}
            valueLabel={convertToDisplayName(effectiveDepVariable) || "Value"}
          />
        </Suspense>
      );
    }

    if (isPDF) {
      return (
        <Suspense fallback={<div className="w-full h-full animate-pulse" />}>
          <PdfChart
            data={scatterData}
            dep={effectiveDepVariable}
            groupBy={effectiveGroupByColumns}
          />
        </Suspense>
      );
    }

    if (isMonteCarlo) {
      return (
        <Suspense fallback={<div className="w-full h-full animate-pulse" />}>
          <MonteCarloChart
            data={scatterData}
            dep={effectiveDepVariable}
            groupBy={effectiveGroupByColumns}
            categories={
              effectiveGroupByColumns.length > 0
                ? scatterData[0]?.categories || []
                : []
            }
          />
        </Suspense>
      );
    }

    if (isSampling) {
      const samplingPayload = applyResponse?.sample_data || [];

      return (
        <Suspense fallback={<div className="w-full h-full animate-pulse" />}>
          <RandomSamplingTable  samplingPayload={samplingPayload}/>
        </Suspense>
      );
    }

    // default: use your existing Z-score scatter chart
    return (
      <Suspense fallback={<div className="w-full h-full animate-pulse" />}>
        <ScatterChart
          data={scatterData}
          flipAxes={false}
          useCategories={effectiveGroupByColumns.length > 0}
          categories={effectiveGroupByColumns.length > 0 ? scatterData[0]?.categories || [] : []}
          groupBy={effectiveGroupByColumns}
          xLabel={
            effectiveGroupByColumns.length > 0
              ? effectiveGroupByColumns.map(convertToDisplayName).join(", ")
              : "Row Index"
          }
          yLabel={convertToDisplayName(effectiveDepVariable) || "Value"}
        />
      </Suspense>
    );
  }, [
    scatterData,
    effectiveDepVariable,
    effectiveGroupByColumns,
    processedData.length,
    models,
  ]);

  const getCurrentConfigSummary = () => {
    if (!effectiveDepVariable) {
      return "No configuration set - please configure chart";
    }

    const displayDepVar = convertToDisplayName(effectiveDepVariable);
    const displayGroupBy = effectiveGroupByColumns
      .map(convertToDisplayName)
      .join(", ");
    return `Dependent: ${displayDepVar} | Group By: ${
      displayGroupBy || "None"
    }`;
  };

  const isEnsembleModelApplied =
    models.includes("ZSCORE") &&
    models.includes("LOF") &&
    models.includes("RSF") &&
    models.includes("BENFORD") &&
    models.includes("PDF") &&
    models.includes("MONTECARLO");

  // Stable handlers to minimize re-renders
  const handleBack = useCallback(() => {
    navigate("/dashboard/anamoly-detection");
  }, [navigate]);

  const handleDownload = useCallback(() => {
    if (applyResponse?.blob_download_link) {
      window.open(applyResponse.blob_download_link, "_blank");
    }
  }, [applyResponse?.blob_download_link]);

  const { ensembleModelStats, ensembleModelDonutData, ensembleModelBarData } =
    useMemo(() => {
      if (!applyResponse) {
        return {
          ensembleModelStats: [],
          ensembleModelDonutData: [],
          ensembleModelBarData: [],
        };
      }

      // --- 1️⃣ Extract summary counts safely ---
      const summary = applyResponse.outlier_summary || {};
      const getNum = (k) => {
        const v = summary?.[k];
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      // --- 2️⃣ Define display names and colors ---
      // --- 3️⃣ Create fake sparkline data for UI ---
      const makeSparkline = (count) => {
        const base = Math.max(
          1,
          Math.round(count / Math.max(1, Math.sqrt(count || 1)))
        );
        return [
          base,
          Math.round(base * 1.1),
          Math.round(base * 0.9),
          Math.round(base * 1.15),
          Math.round(base * 0.95),
          Math.round(base * 1.2),
          Math.round(base * 1.05),
        ];
      };

      // --- 4️⃣ Determine models (fallback order + ensure ensemble last) ---
      const rawModels =
        Array.isArray(applyResponse?.applied_models) &&
        applyResponse.applied_models.length > 0
          ? applyResponse.applied_models
          : [
              "benford",
              "pdf",
              "lof",
              "rsf",
              "zscore",
              "montecarlo",
              "ensemble",
            ];

      const normalizedModels = rawModels.map((m) =>
        String(m || "").toLowerCase()
      );

      // unique ordered
      const uniqueOrdered = [];
      normalizedModels.forEach((m) => {
        if (!uniqueOrdered.includes(m)) uniqueOrdered.push(m);
      });

      // Always include ensemble at end even if missing
      if (!uniqueOrdered.includes("ensemble")) uniqueOrdered.push("ensemble");

      // --- 5️⃣ Build dynamic counts map ---
      const counts = {
        benford: getNum("benford"),
        pdf: getNum("pdf"),
        lof: getNum("lof"),
        rsf: getNum("rsf"),
        zscore: getNum("zscore"),
        montecarlo: getNum("montecarlo"),
        ensemble: getNum("ensemble"),
      };

      const omitZeros = false; // set to true if you want to hide models with 0 count

      // --- 6️⃣ Build ensembleModelStats ---
      const ensembleModelStats = uniqueOrdered
        .filter((k) => !omitZeros || (counts[k] ?? 0) > 0)
        .map((modelKey) => {
          const display = getModelDisplayName(modelKey) || modelKey.toUpperCase();
          const count = counts[modelKey] ?? 0;
          const color = getModelColor(modelKey);
          return {
            key: modelKey,
            title: display,
            count,
            color,
            sparkline: makeSparkline(count),
            subtitle: modelKey === "ensemble" ? "Common Outliers" : "Outliers",
          };
        });

      // --- 7️⃣ Donut Data (include all + ensemble) ---
      const donutOrder = [
        "benford",
        "pdf",
        "lof",
        "rsf",
        "zscore",
        "montecarlo",
        "ensemble",
      ];
      const ensembleModelDonutData = donutOrder
        .filter((k) => uniqueOrdered.includes(k))
        .filter((k) => !omitZeros || (counts[k] ?? 0) > 0)
        .map((k) => {
          const display = getModelDisplayName(k) || k;
          return {
            key: k,
            name: display,
            value: counts[k] ?? 0,
            color: getModelColor(k),
          };
        });

      // --- 8️⃣ Bar Data (include all + ensemble last) ---
      const barOrder = [...donutOrder];
      const ensembleModelBarData = barOrder
        .filter((k) => uniqueOrdered.includes(k))
        .filter((k) => !omitZeros || (counts[k] ?? 0) > 0)
        .map((k) => {
          const display = getModelDisplayName(k) || k;
          return {
            key: k,
            name: display,
            value: counts[k] ?? 0,
            color: getModelColor(k),
          };
        });

      return {
        ensembleModelStats,
        ensembleModelDonutData,
        ensembleModelBarData,
      };
    }, [applyResponse]);

  const parseNumber = (v) => {
    if (v === null || v === undefined) return NaN;
    if (typeof v === "number") return v;
    const s = String(v).replace(/,/g, "").trim();
    if (s === "" || s === "-" || s.toLowerCase() === "na") return NaN;
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : NaN;
  };

  const findField = (row, possible) => {
    const keys = Object.keys(row || {});
    for (const p of possible) {
      const normalized = String(p).replace(/\s+/g, "_").toLowerCase();
      const match = keys.find(
        (k) => k.replace(/\s+/g, "_").toLowerCase() === normalized
      );
      if (match) return match;
    }
    return null;
  };

  const useEnsembleScatterData = (applyResponse) => {
    return useMemo(() => {
      if (!applyResponse || !Array.isArray(applyResponse.sample_data))
        return [];

      const rows = applyResponse.sample_data;
      const first = rows[0] || {};

      // Find Record ID key
      const recordIdKey =
        findField(first, ["Record ID", "RecordID", "record_id", "id"]) || null;

      // Collect all Outlier_* keys (except Ensemble)
      const outlierKeys = Object.keys(first).filter((k) =>
        /^outlier_/i.test(k)
      );

      if (!outlierKeys.length || !recordIdKey) return [];

      // Build flattened points
      const points = [];
      rows.forEach((row, rowIndex) => {
        const recordId = row[recordIdKey] ?? rowIndex + 1;

        outlierKeys.forEach((outKey) => {
          const rawVal = row[outKey];
          const val = parseNumber(rawVal);
          const x = Number.isFinite(val) ? val : 0;

          // exception = 1 means flagged as outlier
          const exception =
            val === 1 || String(val) === "1" || val === true ? 1 : 0;

          const modelKey = normalizeModelKey(outKey);
          const groupLabel =
            getModelDisplayName(modelKey) ||
            outKey.replace("Outlier_", "").replace(/_/g, " ");

          points.push({
            x, // Outlier_* flag value (mostly 0/1)
            y: recordId, // Record ID on Y-axis
            exception,
            id: recordId,
            group: groupLabel,
            modelKey,
            raw: row,
          });
        });
      });

      return points;
    }, [applyResponse]);
  };

  const ensembleScatterData = useEnsembleScatterData(applyResponse);

  const ensembleModelTableData = React.useMemo(() => {
    if (!applyResponse || !Array.isArray(applyResponse.sample_data)) return [];

    const raw = applyResponse.sample_data;

    // Normalize function to safely parse 0/1 values
    const getFlag = (obj, key) => {
      const v = obj?.[key];
      return v === 1 || v === "1" || v === true ? 1 : 0;
    };
    
    const filterData = raw.map((row, idx) => {
      const clusterKey = Object.keys(row).find((k) => k.startsWith("cluster_"));
      return {
        id: row["Record_ID"] ?? idx + 1,
        cluster: clusterKey ? row[clusterKey] : null,
        zscore: getFlag(row, "Outlier_ZScore"),
        benford: getFlag(row, "Outlier_Benford"),
        pdf: getFlag(row, "Outlier_Pdf"),
        lof: getFlag(row, "Outlier_LOF"),
        rsf: getFlag(row, "Outlier_RSF"),
        montecarlo: getFlag(row, "Outlier_Montecarlo"),
        ensemble: getFlag(row, "Outlier_Ensemble"),
      };
    });

    
    return filterData;
  }, [applyResponse]);

  return isEnsembleModelApplied ? (
    <Suspense
      fallback={<Skeleton active paragraph={{ rows: 24 }} size="large" />}
    >
      <EnsembleIndex
        ensembleModelStats={ensembleModelStats}
        ensembleModelDonutData={ensembleModelDonutData}
        ensembleScatterData={ensembleScatterData}
        ensembleModelBarData={ensembleModelBarData}
        ensembleModelTableData={ensembleModelTableData}
        applyResponse={applyResponse}
      />
    </Suspense>
  ) : (
    <div className="p-4 md:p-10 !pt-5 space-y-8 bg-[#131314] rounded-2xl shadow-lg">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Back Button */}
        <Button
          type="text"
          className="flex items-center gap-2 font-medium !text-white text-sm sm:!text-lg hover:!text-violet-300 transition"
          icon={<ArrowBigLeft className="w-5 h-5 mt-2" />}
          onClick={handleBack}
        >
          Back to Configuration
        </Button>

        {/* Title Section */}
        <div className="text-center sm:text-right space-y-1 w-full sm:w-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
            <span className="bg-gradient-to-r from-purple-300 via-violet-400 to-purple-500 bg-clip-text text-transparent">
              Anomaly Detection
            </span>{" "}
            Analytics Dashboard
          </h1>

          <p className="text-gray-300 text-sm sm:text-base">
            Real-time anomaly detection — Configure your own analysis
          </p>
        </div>
      </header>
      {/* Tabs */}
      <Tabs
        defaultActiveKey="overview"
        size="large"
        className="custom-tabs animated-tabs"
        items={[
          {
            key: "overview",
            label: (
              <div className="tab-label">
                <LayoutDashboard size={18} />
                <span>Overview</span>
              </div>
            ),
            children: (
              <>
                <div className="mt-3 p-3 bg-[#0d1421] rounded-lg border border-gray-700">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <Text className="text-gray-300 text-sm">
                      <strong className="text-white">
                        Current Configuration:
                      </strong>{" "}
                      {getCurrentConfigSummary()}
                    </Text>
                  </div>
                </div>

                <div className="mb-8 mt-3 p-6 bg-[#0d1421] border border-gray-700 rounded-xl shadow-lg">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="text-gray-300">
                      <span className="font-semibold text-white">
                        Data Points:
                      </span>{" "}
                      {processedData.length}
                    </div>

                    <Button
                      onClick={handleDownload}
                      className="w-full sm:w-auto px-6 py-2 rounded-full font-medium shadow-md bg-white text-slate-800 border hover:bg-slate-50 transition"
                      disabled={!applyResponse?.blob_download_link}
                    >
                      Download Results
                    </Button>
                  </div>
                </div>

                <div className="w-full h-[600px]">{renderChartEl}</div>
              </>
            ),
          },
          {
            key: "insight",
            label: (
              <div className="tab-label">
                <Lightbulb size={18} />
                <span>Insights</span>
              </div>
            ),
            children: (
              <div className="p-6 text-white">
                <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>
                  <Insights answer_markdown={generativeEngineInsightsResult} />
                </Suspense>
              </div>
            ),
          },
          {
            key: "chat",
            label: (
              <div className="tab-label">
                <MessageSquare size={18} />
                <span>Xychat</span>
              </div>
            ),
            children: (
              <div className="p-6 text-white">
                <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>
                  <XychatAssistant />
                </Suspense>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default AnalyticsDashboard;